import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TbArrowRight, TbShoppingCart, TbPlus, TbExternalLink } from 'react-icons/tb';
import Button from '../buttons/Button';
import * as productsMarketplaceService from '../../../services/products.marketplace.service';
import * as merchantMarketplaceService from '../../../services/merchant.marketplace.service';
import { ProductDto, CheckOnboardingDto } from '../../../services/model/marketplace.model';
import { formatEuroCents } from '../../../utils/bankAccountUtil';

interface ProductSalesChartProps {
  onCreateProduct?: () => void;
}

function ProductSalesChart({ onCreateProduct }: ProductSalesChartProps): JSX.Element {
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.overview.productSalesChart',
  });
  const navigate = useNavigate();
  const [products, setProducts] = useState<ProductDto[]>([]);
  const [onboardingStatus, setOnboardingStatus] = useState<CheckOnboardingDto | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch products and onboarding status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsData, onboardingData] = await Promise.all([
          productsMarketplaceService.getProducts(),
          merchantMarketplaceService.checkOnboarding(),
        ]);
        setProducts(productsData);
        setOnboardingStatus(onboardingData);
      } catch (error) {
        console.error('Error fetching product data:', error);
        setProducts([]);
        setOnboardingStatus(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const topProducts = useMemo(() => {
    const sorted = [...products].sort(
      (a, b) => (b.totalRevenueCollected || 0) - (a.totalRevenueCollected || 0),
    );
    return sorted.slice(0, 5);
  }, [products]);

  const handleSeeMore = () => {
    navigate('/financial/marketplace-products');
  };

  const handleCreateProduct = () => {
    if (onCreateProduct) {
      onCreateProduct();
    } else {
      navigate('/financial/marketplace-products');
    }
  };

  const handleOnboardToMarketplace = () => {
    if (onboardingStatus?.onboardingLink) {
      window.open(onboardingStatus.onboardingLink, '_blank');
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('title')}</h2>
          <p className="mt-1 text-sm text-gray-500">
            {t('subtitle', { period: t('periodLabels.monthly') })}
          </p>
        </div>
        <button
          type="button"
          onClick={handleSeeMore}
          className="flex items-center space-x-2 rounded-lg px-4 py-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/5">
          <span>{t('seeAllProducts')}</span>
          <TbArrowRight size={16} />
        </button>
      </div>

      {/* Body */}
      {!loading && (!onboardingStatus || !onboardingStatus.onboarded) ? (
        <div className="py-12 text-center">
          <TbExternalLink className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">{t('empty.onboardTitle')}</h3>
          <p className="mb-6 text-sm text-gray-500">{t('empty.onboardSubtitle')}</p>
          <Button variant="primary-dark" onClick={handleOnboardToMarketplace}>
            {t('empty.ctaOnboard')}
          </Button>
        </div>
      ) : !loading && onboardingStatus?.onboarded && products.length === 0 ? (
        <div className="py-12 text-center">
          <TbShoppingCart className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">{t('empty.title')}</h3>
          <p className="mb-6 text-sm text-gray-500">{t('empty.subtitle')}</p>
          <Button variant="primary-dark" onClick={handleCreateProduct}>
            <TbPlus className="mr-2" />
            {t('empty.cta')}
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">
                  Total collected
                </th>
              </tr>
            </thead>
            <tbody>
              {topProducts.map((product, index) => (
                <tr key={product.id ?? index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    <div className="flex items-center">
                      {product.imageUrl && (
                        <img
                          className="mr-3 h-8 w-8 rounded object-cover"
                          src={product.imageUrl}
                          alt={product.name}
                        />
                      )}
                      <span>{product.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                    {formatEuroCents(product.totalRevenueCollected || 0, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProductSalesChart;
